import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  CONSENT_VERSION,
  onConsentChange,
  onOpenPreferences,
  readConsent,
  saveConsent,
} from "../lib/cookieConsent";

type Category = {
  key: "essential" | "analytics";
  title: string;
  description: string;
  /** Necesare pentru funcționare: nu pot fi refuzate, deci comutatorul e blocat */
  locked?: boolean;
};

const CATEGORIES: Category[] = [
  {
    key: "essential",
    title: "Strict necesare",
    description:
      "Păstrează sesiunea, preferința de limbă și chiar această alegere. Fără ele site-ul nu funcționează, așa că nu pot fi dezactivate.",
    locked: true,
  },
  {
    key: "analytics",
    title: "Analitice",
    description:
      "PostHog: ce pagini sunt vizitate și ce butoane se apasă, ca să vedem ce funcționează. Dacă le refuzi, scriptul nu se încarcă deloc.",
  },
];

/**
 * Banda de consimțământ, cu panou de personalizare pe categorii.
 *
 * Apare singură doar dacă nu există o alegere salvată pentru versiunea curentă.
 * Poate fi redeschisă oricând din subsol, prin openCookiePreferences().
 */
export function CookieConsent() {
  const reduceMotion = useReducedMotion();
  // Citim direct la inițializare, ca să nu setăm stare sincron dintr-un efect
  // (ar produce un render în plus și un flash de bandă).
  const [visible, setVisible] = useState(() => readConsent() === null);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const stopConsent = onConsentChange((consent) => {
      if (consent === null) setVisible(true);
    });

    const stopOpen = onOpenPreferences(() => {
      // Redeschiderea pornește de la alegerile curente, nu de la zero
      setAnalytics(readConsent()?.analytics ?? false);
      setCustomizing(true);
      setVisible(true);
    });

    return () => {
      stopConsent();
      stopOpen();
    };
  }, []);

  const close = () => {
    setVisible(false);
    setCustomizing(false);
  };

  const decide = (allowAnalytics: boolean) => {
    saveConsent(allowAnalytics);
    close();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-modal="false"
          aria-label="Preferințe cookie-uri"
          initial={reduceMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-0 bottom-0 z-[1100] p-3 sm:p-5"
        >
          <div className="mx-auto max-h-[85vh] max-w-3xl overflow-y-auto rounded-[24px] border border-border bg-white p-5 shadow-2xl sm:p-6">
            <p className="text-base font-bold text-text mb-2">
              {customizing ? "Preferințe cookie-uri" : "Cookie-uri pe suuper"}
            </p>

            <p className="text-sm text-text-light leading-relaxed">
              {customizing
                ? "Alege ce categorii accepți. Poți reveni oricând din subsolul paginii."
                : "Folosim cookie-uri necesare ca site-ul să funcționeze. Separat, am vrea să măsurăm cum e folosit, ca să-l îmbunătățim. Alegi tu — până atunci nu pornim nimic de măsurare."}
            </p>

            {customizing && (
              <ul className="mt-5 space-y-3">
                {CATEGORIES.map((category) => {
                  const checked = category.locked ? true : analytics;
                  return (
                    <li
                      key={category.key}
                      className="rounded-2xl border border-border bg-background p-4"
                    >
                      <label className="flex items-start justify-between gap-4">
                        <span className="min-w-0">
                          <span className="block font-semibold text-text">
                            {category.title}
                          </span>
                          <span className="mt-1 block text-sm text-text-light leading-relaxed">
                            {category.description}
                          </span>
                        </span>

                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={category.locked}
                          onChange={(event) => setAnalytics(event.target.checked)}
                          aria-label={
                            category.locked
                              ? `${category.title} — mereu active`
                              : category.title
                          }
                          className="mt-1 h-5 w-5 shrink-0 cursor-pointer rounded-md accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              {customizing ? (
                <>
                  <button
                    type="button"
                    onClick={() => decide(analytics)}
                    className="order-1 min-h-11 flex-1 rounded-xl bg-primary-gradient px-5 py-3 font-semibold text-white shadow-lg shadow-primary/25 sm:order-2"
                  >
                    Salvează preferințele
                  </button>
                  <button
                    type="button"
                    onClick={() => decide(true)}
                    className="order-2 min-h-11 flex-1 rounded-xl border border-border bg-white px-5 py-3 font-semibold text-text hover:bg-background transition-colors sm:order-1"
                  >
                    Accept toate
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => decide(true)}
                    className="order-1 min-h-11 flex-1 rounded-xl bg-primary-gradient px-5 py-3 font-semibold text-white shadow-lg shadow-primary/25 sm:order-3"
                  >
                    Accept toate
                  </button>
                  <button
                    type="button"
                    onClick={() => decide(false)}
                    className="order-2 min-h-11 flex-1 rounded-xl border border-border bg-white px-5 py-3 font-semibold text-text hover:bg-background transition-colors sm:order-1"
                  >
                    Doar necesare
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAnalytics(readConsent()?.analytics ?? false);
                      setCustomizing(true);
                    }}
                    className="order-3 min-h-11 flex-1 rounded-xl border border-border bg-white px-5 py-3 font-semibold text-text hover:bg-background transition-colors sm:order-2"
                  >
                    Personalizează
                  </button>
                </>
              )}
            </div>

            <p className="mt-3 text-xs text-text-muted">
              Detalii în{" "}
              <a
                href="/confidentialitate"
                className="font-medium text-primary hover:underline"
              >
                politica de confidențialitate
              </a>
              . (v{CONSENT_VERSION})
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
